import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  CreateTaskRequest,
  GetActivityLogListResponse,
  GetActivityLogListV1TasksActivityLogsGetParams,
  GetTasksResultsItemsV1TasksResultItemsGetParams,
  GetUpcomingTasksDataV1TasksUpcomingTasksChartGetParams,
  UpdateTaskRequest,
  UpdateTaskResultRequest,
} from 'compliance-api/generated';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { CURRENT_USER_CLIENTS_LIST_QUERY_KEY } from '@/modules/common/queries/clients.ts';
import { INCIDENTS_LIST_QUERY_KEY } from '@/modules/common/queries/incidents.ts';
import {
  CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY,
  CURRENT_USER_TASKS_LIST_QUERY_KEY,
  CURRENT_USER_TASK_QUERY_KEY,
} from '@/modules/common/queries/tasks.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';

export const CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY =
  'vestra-compliance/admin-tasks-upcoming-chart';
export const CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY =
  'vestra-compliance/admin-tasks-activity-logs';
export const TASKS_RESULTS_QUERY_KEY = 'vestra-compliance/admin-tasks-results-items';

export const useUpcomingTasksChartQuery = (
  payload: GetUpcomingTasksDataV1TasksUpcomingTasksChartGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getUpcomingTasksDataV1TasksUpcomingTasksChartGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useTaskResultsItemsQuery = (
  payload: GetTasksResultsItemsV1TasksResultItemsGetParams = {},
  disabled = false,
) => {
  const { page, ...data } = payload;
  return useInfiniteQuery({
    queryKey: [TASKS_RESULTS_QUERY_KEY, JSON.stringify(data), useGetCurrentBusinessId()],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getTasksResultsItemsV1TasksResultItemsGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useTasksActivityLogsQuery = (
  payload: GetActivityLogListV1TasksActivityLogsGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useInfiniteQuery<GetActivityLogListResponse>({
    queryKey: [CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getActivityLogListV1TasksActivityLogsGet({
        ...payload,
        page: pageParam || payload.page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useCreateTaskMutation = (setErrors?: (errors: FormikErrors<FormikValues>) => void) => {
  return useMutation({
    mutationFn: async (payload: CreateTaskRequest) => {
      const response = await withErrorHandling(api.v1.createTaskV1TasksPost(payload), {
        loading: 'Creating task...',
        success: 'Task created',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_CLIENTS_LIST_QUERY_KEY]);
    },
  });
};

export const useUpdateTaskMutation = (setErrors?: (errors: FormikErrors<FormikValues>) => void) => {
  return useMutation({
    mutationFn: async (payload: UpdateTaskRequest) => {
      const response = await withErrorHandling(api.v1.updateTaskV1TasksPatch(payload), {
        loading: 'Updating task...',
        success: 'Task updated',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASK_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeleteTaskMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(api.v1.deleteTaskV1TasksDelete({ id }), {
        loading: 'Archiving task...',
        success: 'Task archived',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useRestoreTaskMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(api.v1.restoreTaskV1TasksRestorePost({ id }), {
        loading: 'Restoring task...',
        success: 'Task restored',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useGeneratePublicTaskLinksMutation = () => {
  return useMutation({
    mutationFn: async ({ taskId, usersIDs }: { taskId: number; usersIDs: number[] }) => {
      const response = await withErrorHandling(
        api.v1.bulkCreatePublicLinksV1TasksBulkCreatePublicLinksPost({
          task_id: taskId,
          users_ids: usersIDs,
        }),
        // {
        //   loading: 'Generating link...',
        //   success: 'Link created',
        // },
      );
      return response.data;
    },
  });
};

export const useSendReminderEmailsMutation = () => {
  return useMutation({
    mutationFn: async ({ taskId, usersIDs }: { taskId: number; usersIDs: number[] }) => {
      const response = await withErrorHandling(
        api.v1.bulkSendStartDateEmailsV1TasksBulkSendStartDateEmailsPost({
          task_id: taskId,
          user_ids: usersIDs,
        }),
        {
          loading: 'Sending emails...',
          success: 'Emails sent',
        },
      );
      return response.data;
    },
  });
};

export const useResendEmailsMutation = () => {
  return useMutation({
    mutationFn: async ({ userId }: { userId: number }) => {
      const response = await withErrorHandling(
        api.v1.resendEmailToUserV1TasksResendEmailUserIdPost({ userId }),
        {
          loading: 'Sending email...',
          success: 'Email sent',
        },
      );
      return response.data;
    },
  });
};

export const useUpdateTaskResultMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateTaskResultRequest) => {
      const response = await withErrorHandling(api.v1.updateTaskResultV1TasksResultPatch(payload), {
        loading: 'Updating task result...',
        success: 'Task result updated',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeleteTaskResultMutation = () => {
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await withErrorHandling(
        api.v1.deleteTaskResultV1TasksResultIdDelete({ id }),
        {
          loading: 'Archiving task result...',
          success: 'Task result archived',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useRestoreTaskResultMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.restoreTaskResultV1TasksResultRestorePost({ id }),
        {
          loading: 'Restoring task result...',
          success: 'Task result restored',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};
